import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './features/auth/AuthContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import AdminAuthLayout from './layouts/AdminAuthLayout';

// Route Guards
import ProtectedStudentRoute from './features/auth/components/ProtectedStudentRoute';
import AdminRoute        from './features/auth/components/AdminRoute';

// Eagerly loaded pages (above-the-fold / auth critical)
import LandingPage from './pages/LandingPage';
import StudentLoginPage from './pages/StudentLoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminLoginPage from './pages/AdminLoginPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import FAQPage from './pages/FAQPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import CancellationPolicyPage from './pages/CancellationPolicyPage';
import ContactUsPage from './pages/ContactUsPage';

// Styles
import './styles/App.css';

// Lazy loaded pages (loaded on demand)
const DashboardPage              = lazy(() => import('./pages/DashboardPage'));
// Practice problems — disabled; this product is assessments-only
// const ProblemsPage               = lazy(() => import('./pages/ProblemsPage'));
// const WalletPage                 = lazy(() => import('./pages/WalletPage'));
// const ExamPage                   = lazy(() => import('./pages/ExamPage'));
// const SubmissionsListPage        = lazy(() => import('./pages/SubmissionsListPage'));
// const SubmissionReviewPage       = lazy(() => import('./pages/SubmissionReviewPage'));
const AssessmentDetailPage       = lazy(() => import('./pages/AssessmentDetailPage'));
const AssessmentExamPage         = lazy(() => import('./pages/AssessmentExamPage'));
const AdminOrgsPage             = lazy(() => import('./pages/AdminOrgsPage'));
const AdminOrgDetailPage        = lazy(() => import('./pages/AdminOrgDetailPage'));
const AdminUsersPage            = lazy(() => import('./pages/AdminUsersPage'));
const AdminStatsPage            = lazy(() => import('./pages/AdminStatsPage'));
const AdminPricingPage          = lazy(() => import('./pages/AdminPricingPage'));

const PageLoader = () => <LoadingSpinner size="md" label="Loading…" fullPage />;

function App() {
  return (
    <Router>
      <AuthProvider>
        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ── Public ─────────────────────────────────────────── */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/cancellation-policy" element={<CancellationPolicyPage />} />
            <Route path="/contact-us" element={<ContactUsPage />} />

            {/* ── Student Auth ───────────────────────────────────── */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<StudentLoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/login/otp" element={<Navigate to="/login" replace />} />
            </Route>

            {/* ── Email Verification (public, no layout) ─────────── */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />

            {/* ── Learning Module (MainLayout) ────────────────── */}
            <Route element={<MainLayout />}>
              <Route path="/dashboard" element={
                <ProtectedStudentRoute><DashboardPage /></ProtectedStudentRoute>
              } />
              {/* Practice problems — disabled; this product is assessments-only
              <Route path="/problems" element={
                <ProtectedStudentRoute><ProblemsPage /></ProtectedStudentRoute>
              } />
              <Route path="/wallet" element={
                <ProtectedStudentRoute><WalletPage /></ProtectedStudentRoute>
              } />
              */}
            </Route>

            {/* ── Exam / Review (full-page, no layout) ────────── */}
            {/* Practice problem exam/review — disabled; this product is assessments-only
            <Route path="/exam/:problemId" element={
              <ProtectedStudentRoute><ExamPage /></ProtectedStudentRoute>
            } />
            <Route path="/problems/:problemId/submissions" element={
              <ProtectedStudentRoute><SubmissionsListPage /></ProtectedStudentRoute>
            } />
            <Route path="/submission/:submissionId" element={
              <ProtectedStudentRoute><SubmissionReviewPage /></ProtectedStudentRoute>
            } />
            */}
            <Route path="/assessments/:assessmentId" element={
              <ProtectedStudentRoute><AssessmentDetailPage /></ProtectedStudentRoute>
            } />
            <Route path="/assessments/:assessmentId/attempt/:attemptId" element={
              <ProtectedStudentRoute><AssessmentExamPage /></ProtectedStudentRoute>
            } />

            {/* ── Admin Login (separate portal, not regular AuthLayout) */}
            <Route element={<AdminAuthLayout />}>
              <Route path="/admin/login" element={<AdminLoginPage />} />
            </Route>

            {/* ── Admin Panel ────────────────────────────────────── */}
            <Route path="/admin" element={
              <AdminRoute><AdminDashboardPage /></AdminRoute>
            } />
            <Route path="/admin/orgs" element={
              <AdminRoute><AdminOrgsPage /></AdminRoute>
            } />
            <Route path="/admin/orgs/:id" element={
              <AdminRoute><AdminOrgDetailPage /></AdminRoute>
            } />
            <Route path="/admin/users" element={
              <AdminRoute><AdminUsersPage /></AdminRoute>
            } />
            <Route path="/admin/stats" element={
              <AdminRoute><AdminStatsPage /></AdminRoute>
            } />
            <Route path="/admin/pricing" element={
              <AdminRoute><AdminPricingPage /></AdminRoute>
            } />

            {/* ── Fallback ───────────────────────────────────────── */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </AuthProvider>
    </Router>
  );
}

export default App;
